import { useFormContext } from "react-hook-form";

import {
    useComprarButton,
    useReserve
} from "../../hooks";

import { useEvento } from "@/store/evento/evento";

import {
    useWizardContext,
    useEventoContext
} from "../../contexts";

import {
    CompraCompletada,
    DatosComprador,
    MetodoPagoTarjeta,
    MetodoPagoAllaria,
    SeleccionarGateway,
    Temporizador,
    ResumenCompra
} from "../steps";

import { CheckoutPayload } from "@/schemas/checkout.schema";
import { checkout } from "@/services/payway.service";
import { logPaymentError, logInfo } from "@/services/log.service";
import { setCompraAccessToken } from "@/helpers/compraAccessToken";
import { WIZARD_STEPS, STEPS_WITH_TIMER, WizardStep } from "../../constants";
import { trackInitiateCheckout } from "@/lib/utils/metaPixel";

interface PaymentWizardStepsProps {
    onClose: () => void;
}

// ============================================
// Steps
// ============================================
export const PaymentWizardSteps = ({ onClose }: PaymentWizardStepsProps) => {
    const methods = useFormContext<CheckoutPayload>();

    const { step, next, moveTo } = useWizardContext();
    const { evento, mapa } = useEventoContext();

    const { reservaId } = useReserve();

    const { totalWithServiceFee } = useComprarButton(mapa);

    const { state: compraStatus, set: setCompraStatus } = useEvento.use.compra();
    const { state: entradas, set: setEntradas } = useEvento.use.entradas();
    const { state: funcion } = useEvento.use.funcion();
    const { set: setIsLoading } = useEvento.use.isLoading();
    const { state: gateway } = useEvento.use.gateway();
    const { set: setComprador } = useEvento.use.comprador();



    // ============================================
    // Handlers
    // ============================================
    const handleTimeout = async () => {
        // El release lo hace handlePanelClose (que es onClose)
        // Solo navegamos y cerramos
        moveTo(WIZARD_STEPS.RESUMEN);
        onClose();
    };

    const handleComprador = async () => {
        const comprador = methods.watch("comprador");
        const ok = await methods.trigger("comprador");
        if (!ok) return;

        setComprador(comprador);

        if (totalWithServiceFee === 0) {
            setIsLoading(true);
            setCompraStatus({
                status: 'en proceso',
                message: 'Generando tu compra gratuita...',
                compraId: null,
            });

            if (!comprador || !reservaId) {
                setIsLoading(false);
                setCompraStatus({
                    status: 'rechazada',
                    message: 'No pudimos procesar tu compra',
                    compraId: null,
                });
                moveTo(WIZARD_STEPS.COMPLETADO);
                return;
            }

            let response;
            try {
                response = await checkout({
                    comprador,
                    entradas,
                    funcionId: funcion!.id,
                    eventoId: evento.id,
                    reservaId
                });
            } catch (err: any) {
                setIsLoading(false);
                setCompraStatus({
                    status: 'rechazada',
                    message: err?.message ?? 'No pudimos procesar tu compra',
                    compraId: null,
                });
                moveTo(WIZARD_STEPS.COMPLETADO);
                return;
            }

            setIsLoading(false);

            const data = response?.data || response;

            if (!data.compraId) {
                setCompraStatus({
                    status: 'rechazada',
                    message: 'No pudimos procesar tu compra',
                    compraId: null,
                });
                moveTo(WIZARD_STEPS.COMPLETADO);
                return;
            }

            setCompraStatus({
                status: 'confirmada',
                message: 'Compra confirmada',
                compraId: String(data.compraId),
                accessToken: data.accessToken,
                tickets: !('mode' in data) ? data.tickets : undefined,
            });
            setEntradas([]);
            moveTo(WIZARD_STEPS.COMPLETADO);
            return;
        }

        // Usuario confirmó datos y avanza al formulario de tarjeta → InitiateCheckout.
        // En este punto el usuario ya interactuó y fbq lleva tiempo inicializado.
        trackInitiateCheckout();
        
        logInfo({
            message: 'Avanzando a método de pago (reserva activa)',
            email: comprador?.email,
            reservaId
        });

        next();
    };

    const handlePaymentSuccess = (resp: any) => {
        const data = resp?.data || resp;
        const compraId = data?.compraId;
        const accessToken = data?.accessToken;

        if (accessToken) {
            /*
               localStorage: source of truth de larga vida para descargar PDF,
               ver tracks, etc. desde cualquier pestaña del browser.
            */
            setCompraAccessToken(compraId, accessToken);
        }
        
        logInfo({
            message: 'Compra EXITOSA (Paso final Frontend)',
            compraId,
            reservaId
        });

        setCompraStatus({
            status: 'en proceso',
            message: 'Validando la transacción...',
            compraId: String(compraId),
            accessToken,
        });
    };

    const handlePaymentLoading = () => {
        setIsLoading(true);
        
        logInfo({
            message: 'Iniciando checkout (Usuario tocó Comprar)',
            reservaId
        });

        setCompraStatus({
            status: 'nueva',
            message: 'Procesando tu compra...',
            compraId: null,
        });
    };

    const handlePaymentError = (err: any) => {
        // Mensaje técnico para el log (detalle real del error)
        const bodyMsg = err?.body?.msg || err?.body?.error || err?.body?.message;
        const logMessage = bodyMsg ?? err?.message ?? 'Error desconocido';

        // Mensaje amigable para el usuario (nunca exponer internos)
        const userMessage = 'No pudimos procesar tu compra';

        logPaymentError({
            message: logMessage,
            gateway,
            step,
            eventoId: evento.id,
            funcionId: funcion?.id,
            reservaId,
            monto: totalWithServiceFee,
            status: err?.status,
            errorDetail: err?.body ? JSON.stringify(err.body) : undefined,
        } as any);

        setCompraStatus({
            status: 'rechazada',
            message: userMessage,
            compraId: null,
        });
        moveTo(WIZARD_STEPS.COMPLETADO);
    };

    // ============================================
    // Configuración de pasos
    // ============================================
    const stepsConfig = {
        [WIZARD_STEPS.RESUMEN]: (
            <ResumenCompra
                nombre={evento.nombre}
                flyer={evento.flyer ?? "/bebop/placeholder/flyer_ticketera.png"}
                entradas={entradas}
                onClick={next}
            />
        ),
        [WIZARD_STEPS.DATOS_COMPRADOR]: (
            <DatosComprador onClick={handleComprador} />
        ),
        [WIZARD_STEPS.SELECTOR_GATEWAY]: (
            <SeleccionarGateway />
        ),
        [WIZARD_STEPS.METODO_PAGO]: gateway === 'allaria' ? (
            <MetodoPagoAllaria amount={totalWithServiceFee} onError={handlePaymentError} />
        ) : (
            <MetodoPagoTarjeta
                amount={totalWithServiceFee}
                onSuccess={handlePaymentSuccess}
                onLoading={handlePaymentLoading}
                onError={handlePaymentError}
            />
        ),
        [WIZARD_STEPS.COMPLETADO]: (
            <CompraCompletada
                status={compraStatus.status === 'nueva' || compraStatus.status === 'en proceso' ? null : compraStatus.status}
                message={compraStatus.message}
                tickets={compraStatus.tickets}
                pdfUrl={compraStatus.pdfUrl}
                compraId={compraStatus.compraId}
            />
        ),
    };

    const showTimer = STEPS_WITH_TIMER.includes(step as WizardStep);

    // ============================================
    // Render
    // ============================================
    return (
        <>
            {showTimer && (
                <Temporizador
                    duracionMinutos={10}
                    onTimeout={handleTimeout}
                />
            )}
            {stepsConfig[step as WizardStep]}
        </>
    );
}